// P3 W2 B8 (FS §7) — design-class derivation + the designed-for-class
// mismatch warning chip.
//
// Shared between the standalone ComposerDetailPage header (detail.tsx —
// tests mount it directly) and the ComposerEditor hero (composer-editor.tsx —
// the production /composers/:id route) so the derivation + warning logic
// cannot drift between the two surfaces.
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PLAYER_CLASS_PROFILES,
  PlayerClasses,
  type CapabilityMatrix,
  type PlayerClass,
} from '@displaycms/contracts';

// P3 W2 B8 (FS §7) — derive the composer's intended "design class".
//
// Neither the composer GET response (ComposerDetailDto, server
// composers.service.ts#get) nor the admin `ComposerDetail` type carry a
// design-class field OR the composer's own `min_capability_flags`. The only
// place that data surfaces on detail mount is the B6 capability matrix's
// per-version `min_capability_flags`. So we DERIVE the design class:
//
//   1. Among `matrix.versions`, consider the non-default-profile versions
//      that declare a non-empty `min_capability_flags`. Take the FIRST such
//      version in server array order (deterministic).
//   2. For its flag set, pick the class c ∈ [A,B,C,D] whose
//      PLAYER_CLASS_PROFILES[c].expected_capability_flags is a SUPERSET of
//      those flags, choosing the class with the SMALLEST expected-flag count
//      (the minimal/tightest profile); tie-break by A→B→C→D order.
//   3. If no such version exists, or no class is a superset → null (no
//      design-class claim → no warning chip).
export function deriveDesignClass(
  versions: ReadonlyArray<{
    min_capability_flags: string[] | null;
    is_default_profile: boolean;
  }>,
): PlayerClass | null {
  const target = versions.find(
    (v) =>
      !v.is_default_profile &&
      !!v.min_capability_flags &&
      v.min_capability_flags.length > 0,
  );
  if (!target || !target.min_capability_flags) return null;
  const needed = target.min_capability_flags;
  let best: PlayerClass | null = null;
  let bestSize = Infinity;
  for (const c of PlayerClasses) {
    const expected = PLAYER_CLASS_PROFILES[c].expected_capability_flags;
    const expectedSet = new Set(expected);
    if (!needed.every((f) => expectedSet.has(f))) continue;
    if (expected.length < bestSize) {
      best = c;
      bestSize = expected.length;
    }
  }
  return best;
}

export interface ClassMismatchChipProps {
  /** The B6 capability matrix (undefined while loading/errored → no chip). */
  matrix: CapabilityMatrix | undefined;
  lang: 'fr' | 'en';
}

// P3 W2 B8 — designed-for-class mismatch warning. The composer's design
// class is derived from the B6 matrix (no detail field carries it). The
// chip fires only when a design class is derivable AND at least one
// assigned player has a KNOWN class that differs from it (unknown-class
// players are never a mismatch). The interpolated player class is the
// FIRST such mismatching player's class.
export function ClassMismatchChip({
  matrix,
  lang,
}: ClassMismatchChipProps): JSX.Element | null {
  const { t } = useTranslation(['composers']);

  const designClass = useMemo(
    () => (matrix ? deriveDesignClass(matrix.versions) : null),
    [matrix],
  );
  const mismatchPlayer = useMemo(() => {
    if (!matrix || designClass === null) return undefined;
    return matrix.players.find(
      (p) => p.player_class !== null && p.player_class !== designClass,
    );
  }, [matrix, designClass]);

  if (designClass === null || !mismatchPlayer) return null;

  return (
    <span
      data-testid="composer-class-warning"
      className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
    >
      {t('composers:classprofile.warning', {
        designClass: PLAYER_CLASS_PROFILES[designClass].label_i18n[lang],
        playerClass:
          mismatchPlayer.player_class === null
            ? ''
            : PLAYER_CLASS_PROFILES[mismatchPlayer.player_class]
                .label_i18n[lang],
      })}
    </span>
  );
}
